import React, { useState } from 'react';
import { Box, Tab } from '@mui/material';
import { MemoReadOnlyJsonEditor } from '../../../../../components';
import GeneralDetailsAccordion from './general-details-accordion/GeneralDetailsAccordion';
import {
    DetailAccordion,
    detailAccordions,
    GENERAL_DETAILS_TABS,
    GeneralDetailsSDO,
    generalDetailStyles,
    NESTED_TABLE_LEVEL_WARNING,
} from './details-components-helpers';
import { Tabs } from '@biq/apex-ui';
import CustomDetailsTabPanel from './CustomDetailsTabPanel';

const GeneralDetail = (props: any): React.JSX.Element => {
    const [tabValue, setTabValue] = React.useState(0);
    const [expanded, setExpanded] = useState<string | boolean>('');
    const handleChange = (isExpanded: boolean, panel: string): void => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: number
    ): void => {
        setTabValue(newValue);
    };

    const detailsSDO: GeneralDetailsSDO = props?.data?.sdo;
    const dictionaries = props?.dictionaries ?? [];
    const dictionaryName = props?.dictionaryName ?? '';
    const dictionaryDetails = dictionaries.find(
        (dictionary: any) => dictionary.name === dictionaryName
    );
    const dictionaryIndexes = dictionaryDetails?.indexes ?? [];
    const primaryKeyAttributes: string[] =
        dictionaryIndexes.find((index: any) => index.type === 'PRIMARY')
            ?.attributes ?? [];
    const { data, header, metadata, verifications } = detailsSDO;

    const getSortedData = (): any => {
        if (!primaryKeyAttributes) return data;

        // Pseudo vertices (nodes) don't have any data. Therefore, fail early and return undefined to display the empty state.
        if (!data) return undefined;

        let primaryKeyAttributesWithLabelIndicator = {};

        // We want to mark any primary key attributes with an asterisk for visual representation.
        primaryKeyAttributes.forEach((attribute: string) => {
            Object.entries(data).forEach(([key, value]) => {
                if (attribute === key) {
                    primaryKeyAttributesWithLabelIndicator = {
                        ...primaryKeyAttributesWithLabelIndicator,
                        [`${key} *`]: value,
                    };
                }
            });
        });

        // sort all the non-primary key attributes alphabetically.
        const sortedNonPrimaryKeyAttributes = Object.entries(data)
            .sort(([keyA], [keyB]) => {
                return keyA.toLowerCase().localeCompare(keyB.toLowerCase());
            })
            .reduce((acc: any, [key, value]) => {
                const isDictionaryIndex = primaryKeyAttributes.find(
                    (attribute: string) => attribute === key
                );

                // only add the attributes that are not part of the primary key.
                if (!isDictionaryIndex) {
                    acc[key] = value;
                }

                return acc;
            }, {});

        /**
         * We want to return the data in the following order:
         *
         * 1. We first want to display the primary key attributes in the same order they are coming from the dictionary editor.
         * 2. We display the non-primary attributes sorted alphabetically.
         */
        return {
            ...primaryKeyAttributesWithLabelIndicator,
            ...sortedNonPrimaryKeyAttributes,
        };
    };

    const accordions: DetailAccordion[] = [
        { ...detailAccordions[0], data: getSortedData() },
        { ...detailAccordions[1], data: header },
        { ...detailAccordions[2], data: metadata },
        { ...detailAccordions[3], data: verifications },
    ];

    return (
        <Box sx={generalDetailStyles.generalDetailsContainer}>
            <Box sx={generalDetailStyles.tabsContainer}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label='node details tabs'
                    orientation='horizontal'
                    color='secondary'
                    variant='standard'
                    tabsType='filled'
                    withIcon={false}
                    sx={generalDetailStyles.tabs}
                >
                    <Tab label={GENERAL_DETAILS_TABS.INFO} />
                    <Tab label={GENERAL_DETAILS_TABS.JSON} />
                </Tabs>
            </Box>
            <CustomDetailsTabPanel value={tabValue} index={0}>
                {accordions.map((accordion) => (
                    <GeneralDetailsAccordion
                        key={`${accordion.name}_${accordion.accordionSumID}`}
                        expanded={expanded === accordion.name}
                        onChange={(_, isExpanded): void =>
                            handleChange(isExpanded, accordion.name)
                        }
                        accordionAriaControls={accordion.accordionAriaControls}
                        accordionSumID={accordion.accordionSumID}
                        accordionHeader={accordion.accordionHeader}
                        data={accordion.data}
                    />
                ))}
                <Box sx={generalDetailStyles.warningContainer}>
                    <span style={generalDetailStyles.warningMessage}>
                        {NESTED_TABLE_LEVEL_WARNING}
                    </span>
                </Box>
            </CustomDetailsTabPanel>
            <CustomDetailsTabPanel value={tabValue} index={1}>
                <Box sx={generalDetailStyles.jsonContainer}>
                    <MemoReadOnlyJsonEditor jsonData={detailsSDO} />
                </Box>
            </CustomDetailsTabPanel>
        </Box>
    );
};

export default GeneralDetail;
